import React, { useMemo } from 'react';
import {
    Box,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    MenuItem,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGears } from '@fortawesome/pro-solid-svg-icons';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import {
    SETTINGS_BASE_PATH,
    SETTINGS_GROUP_ORDER,
    SettingsGroup,
    SettingsSection,
    settingsSectionPath,
    visibleSettingsSections,
} from './settingsSections';

/** Breite der Bereichsspalte. Reicht für die längsten Bezeichnungen in allen Sprachen. */
const NAV_WIDTH = 260;

/**
 * Rahmen der Verwaltung unter <c>/admin/settings</c>: links die Bereiche, rechts die
 * jeweilige Seite.
 *
 * Bewusst eine Liste und keine Tabs — Tabs brechen schon bei sechs Einträgen um, und die
 * Zahl der Bereiche wächst weiter. Auf schmalen Displays wird aus der Liste eine Auswahl,
 * damit die Seite nicht zweispaltig gequetscht wird.
 */
export const AdminSettingsLayout: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const context = useApplicationContext();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

    const sections = useMemo(
        () => visibleSettingsSections(context.userData!.userGroup!),
        [context.userData],
    );

    /** Der Bereich, dessen Pfad am besten zur aktuellen URL passt. Leer auf der Übersicht. */
    const activePath = useMemo(() => {
        const match = sections
            .map(settingsSectionPath)
            .filter((path): path is string => path !== undefined)
            .find((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
        return match ?? SETTINGS_BASE_PATH;
    }, [location.pathname, sections]);

    const grouped = useMemo(() => {
        const map = new Map<SettingsGroup, SettingsSection[]>();
        sections.forEach((section) => {
            // Bereiche ohne Detailseite erscheinen nur als Kachel in der Übersicht,
            // nicht in der Navigation — ein Eintrag, der nirgends hinführt, verwirrt nur.
            if (!section.path) return;
            const list = map.get(section.group) ?? [];
            list.push(section);
            map.set(section.group, list);
        });
        return map;
    }, [sections]);

    const navigation = isNarrow ? (
        <TextField
            select
            fullWidth
            size="small"
            label={t('settings.area')}
            value={activePath}
            onChange={(event) => navigate(event.target.value)}
            sx={{ mb: 2 }}
        >
            <MenuItem value={SETTINGS_BASE_PATH}>{t('settings.overview.title')}</MenuItem>
            {SETTINGS_GROUP_ORDER.flatMap((group) =>
                (grouped.get(group) ?? []).map((section) => (
                    <MenuItem key={section.key} value={settingsSectionPath(section)!}>
                        {t(section.labelKey)}
                    </MenuItem>
                )),
            )}
        </TextField>
    ) : (
        <Paper sx={{ width: NAV_WIDTH, flexShrink: 0, alignSelf: 'flex-start', overflow: 'hidden' }}>
            <List component="nav" dense sx={{ py: 0 }}>
                <ListItemButton
                    selected={activePath === SETTINGS_BASE_PATH}
                    onClick={() => navigate(SETTINGS_BASE_PATH)}
                >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <FontAwesomeIcon icon={faGears} />
                    </ListItemIcon>
                    <ListItemText primary={t('settings.overview.title')} />
                </ListItemButton>
                <Divider />
                {SETTINGS_GROUP_ORDER.map((group) => {
                    const items = grouped.get(group) ?? [];
                    if (items.length === 0) return null;
                    return (
                        <React.Fragment key={group}>
                            <ListSubheader disableSticky sx={{ lineHeight: '2.2rem' }}>
                                {t(`settings.groups.${group}`)}
                            </ListSubheader>
                            {items.map((section) => {
                                const path = settingsSectionPath(section)!;
                                return (
                                    <ListItemButton
                                        key={section.key}
                                        selected={activePath === path}
                                        onClick={() => navigate(path)}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <FontAwesomeIcon icon={section.icon} />
                                        </ListItemIcon>
                                        <ListItemText primary={t(section.labelKey)} />
                                    </ListItemButton>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </List>
        </Paper>
    );

    return (
        <Box>
            <Typography component="h1" variant="h5" color="primary" gutterBottom>
                <FontAwesomeIcon icon={faGears} style={{ marginRight: 12 }} />
                {t('settings.title')}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isNarrow ? 'column' : 'row',
                    gap: 2,
                    alignItems: 'stretch',
                }}
            >
                {navigation}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default AdminSettingsLayout;
